import type { ReactNode } from 'react';
import { isUnauthenticated } from './api';
import { WORK_ORDER_STATUS_LABELS, type WorkOrderStatus } from '../../shared/work-order-status';

/**
 * Shared primitives for the operations UI.
 *
 * Dense and functional by intent (brief §6): small type, tight rows, tabular
 * numerals, and a visible focus ring on everything interactive so the whole
 * app is usable from the keyboard.
 */

const STATUS_TONE: Record<WorkOrderStatus, string> = {
  draft: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  pending_review: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  dispatched: 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200',
  acknowledged: 'bg-indigo-100 text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-200',
  in_progress: 'bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-200',
  submitted: 'bg-teal-100 text-teal-900 dark:bg-teal-900/40 dark:text-teal-200',
  approved: 'bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-200',
  closed: 'bg-neutral-200 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400',
  rejected: 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200',
  cancelled: 'bg-neutral-100 text-neutral-500 line-through dark:bg-neutral-900 dark:text-neutral-500',
};

export function StatusBadge({ status }: { status: WorkOrderStatus }) {
  return (
    <span className={`inline-block whitespace-nowrap px-1.5 py-0.5 text-xs font-medium ${STATUS_TONE[status]}`}>
      {WORK_ORDER_STATUS_LABELS[status]}
    </span>
  );
}

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'danger';
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  const tone =
    variant === 'primary'
      ? 'bg-harkey-gold text-black hover:brightness-95 border-harkey-gold font-semibold'
      : variant === 'danger'
        ? 'bg-white text-red-700 border-red-300 hover:bg-red-50 dark:bg-neutral-900 dark:text-red-300 dark:border-red-800'
        : 'bg-white text-neutral-800 border-neutral-300 hover:bg-neutral-50 dark:bg-neutral-900 dark:text-neutral-200 dark:border-neutral-700 dark:hover:bg-neutral-800';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`border px-2.5 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-40 ${tone}`}
    >
      {children}
    </button>
  );
}

export function Panel({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="border border-neutral-200 dark:border-neutral-800">
      <header className="flex items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-3 py-1.5 dark:border-neutral-800 dark:bg-neutral-900">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400">
          {title}
        </h2>
        <div className="ml-auto flex gap-1.5">{actions}</div>
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 border-b border-neutral-100 py-1 last:border-0 dark:border-neutral-900">
      <dt className="w-40 shrink-0 text-xs text-neutral-500">{label}</dt>
      <dd className="min-w-0 flex-1 text-xs">{children ?? '—'}</dd>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-xs text-neutral-500">{children}</p>;
}

/**
 * `error` accepts the thrown value rather than a string so the banner can tell
 * an expired session from a real failure and offer the way out of it.
 */
export function ErrorBanner({ error }: { error: unknown }) {
  const expired = isUnauthenticated(error);
  const message = expired
    ? 'Your session has expired.'
    : error instanceof Error
      ? error.message
      : String(error);

  return (
    <div
      role="alert"
      className="mb-3 flex flex-wrap items-center gap-3 border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
    >
      <span>{message}</span>
      {expired && (
        <button
          onClick={() => window.location.reload()}
          className="border border-red-400 bg-white px-2 py-0.5 font-medium text-red-800 hover:bg-red-100 dark:bg-red-900 dark:text-red-100"
        >
          Sign in again
        </button>
      )}
    </div>
  );
}

export function Spinner() {
  return <p className="py-8 text-center text-xs text-neutral-500">Loading…</p>;
}
